
import React from 'react';
import { flushSync } from 'react-dom';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import axios from 'axios';
import { format, formatISO, parseISO, addMonths } from 'date-fns';
import { useSnackbar } from 'notistack';
import { Types } from 'utils/constants';
import useRecordStore from 'contexts/record-store';
import Card from 'components/card';
import FeatureBox from 'components/feature-box';
import Icon from 'components/icon';


const useStyles = makeStyles(theme => ({
  root: {
    minWidth: theme.spacing(40),
    backgroundColor: 'transparent',
    '& .MuiDialogActions-spacing > :not(:first-child)': {
      [theme.breakpoints.down('sm')]: {
        marginLeft: '0px',
      },
    },
  },
  titleIcon: {
    marginRight: theme.spacing.dialogTitleIconMargin,
  },
  eventName: {
    lineHeight: 1.75
  },
  eventDetail: {
    fontSize: theme.typography.sizes[0],
    color: theme.palette.sys.onSurfaceVariant,
    lineHeight: 1.4
  },
  actionButton: {
    minWidth: theme.spacing(10),
    [theme.breakpoints.down('sm')]: {
      minHeight: theme.spacing(6),
    },
  },
  loadingWrapper: {
    position: 'relative'
  },
  spinner: {
    position: 'absolute',
    top: 2,
    left: '40%',
    height: '90%'
  }
}));


const MoveToEventDialog = (props) => {
  const { exerciseId, eventId, onClose, setIsLoading } = props;
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const recordStore = useRecordStore();
  const exerciseRecord = recordStore.useRecord(Types.Record.EXERCISE, exerciseId).ornull;
  const eventRecord = recordStore.useRecord(Types.Record.EVENT, eventId).ornull;
  const sourceDate = parseISO(eventRecord.get().date);
  const startDate = formatISO(addMonths(sourceDate, -3));
  const endDate = formatISO(addMonths(sourceDate, 3));
  // state
  const [open, setOpen] = React.useState(true);
  const [selectedEventId, selectEvent] = React.useState('');
  const [events, setEvents] = React.useState([]);
  const [loadingEvents, setLoadingEvents] = React.useState(false);


  React.useEffect(() => {
    if (open) {
      setLoadingEvents(true);
      axios.get(`/training/event/editable?startDate=${startDate}&endDate=${endDate}`)
        .then(response => {
          if (response.status === 200) {
            setEvents(response.data);
            selectEvent('');
          }
        })
        .catch((error) => {
          enqueueSnackbar('Error fetching available events', {
            variant: 'error',
            persist: true,
            anchorOrigin: {
              vertical: "top",
              horizontal: "center"
            }
          });
        })
        .finally(() => {
          setLoadingEvents(false);
        });
    }
  }, [open, exerciseId, startDate, endDate, enqueueSnackbar]);


  const onSubmit = async () => {
    setIsLoading(true);
    setOpen(false);
    try {
      await recordStore.changeExerciseEvent(exerciseRecord.get().id, selectedEventId);
      enqueueSnackbar('Exercise moved', {
        variant: 'success'
      });

    } catch (error) {
      let errmsg = 'Error moving exercise';

      if (error.response && error.response.status === 403) {
        errmsg = 'You are not authorized to edit the source or destination event';
      }

      enqueueSnackbar(errmsg, {
        variant: 'error',
        persist: true,
        anchorOrigin: {
          vertical: "top",
          horizontal: "center"
        }
      });

    } finally {
      flushSync(() => {
        setIsLoading(false);
      });
      onClose();
    }
  }


  const onCancel = () => {
    setOpen(false);
    setTimeout(() => onClose(), 100);
  }

  
  return (
    <>
      {exerciseRecord &&
      <Dialog
        open={open}
        onClose={onCancel}
        classes={{paper: classes.root}}
        aria-labelledby="move-to-event-title"
        aria-describedby="move-to-event-content">

        <Card 
          titleStyle="dark" 
          title={<><Icon className={classes.titleIcon} type="transfer"/>Move Exercise</>} 
          id="move-to-event-title"
        >
          <FeatureBox loading={loadingEvents}>

            <DialogContent id="move-to-event-content">
              <DialogContentText style={{marginBottom: '16px'}}>
                Move the exercise "{ exerciseRecord.get().name }" to the following event:
              </DialogContentText>

              <div className={classes.loadingWrapper}>
                <FormControl
                  variant="outlined"
                  fullWidth
                  size="small"
                  disabled={loadingEvents}
                  style={{ flex: 1 }}
                >
                  <InputLabel id="target-training-event">Select Event</InputLabel>
                  <Select
                    labelId="target-training-event"
                    label="Select Event"
                    value={selectedEventId}
                    onChange={e => selectEvent(e.target.value)}>
                      { events.map( event =>
                        <MenuItem value={event.id} key={event.id}>
                          <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <Typography variant="body2" className={classes.eventName}>{event.name}</Typography>
                            <Typography variant="subtitle2" className={classes.eventDetail}>
                              {event.location.locationName}
                            </Typography>
                            <Typography variant="subtitle2" className={classes.eventDetail}>
                              {format(parseISO(event.date), 'MMM dd, yyyy h:mm aaa')}
                            </Typography>
                          </div>
                        </MenuItem> )}
                  </Select>
                </FormControl>
              </div>
            </DialogContent>

            <DialogActions>
              <Button onClick={onCancel}>Cancel</Button>
              <Button
                  color="primary"
                  className={classes.actionButton}
                  disabled={!selectedEventId}
                  onClick={onSubmit}>
                  OK
              </Button>
            </DialogActions>

          </FeatureBox>
        </Card>
      </Dialog>}
    </>
  )
}

export default MoveToEventDialog;